/**
 * Session operations (spec §4.1, §7 — B20b(h) ruling 2026-07-14).
 *
 * The GUEST DOOR: guest-session mint, NOT anonymous operations. A buyer
 * with no credential calls `session.guest` and receives a short-lived
 * CUSTOMER-scoped token bound to a FRESH guest identity; every subsequent
 * call is an authenticated CUSTOMER like any other (Inv 10 walls intact).
 * Rate limiting and the mint audit row are the door's walls — TTL, limit,
 * and audit semantics are pinned by B24-style adversarial tests.
 */

import { z } from "zod";
import { zChannelTheme } from "./channels.js";
import { publicOp, zEpochMs, zId } from "./common.js";
import { GTM_CONTAINER_ID } from "./gtm.js";

export const sessionGuest = publicOp({
  name: "session.guest",
  permission: "public",
  fields: { channelId: zId },
  response: z
    .object({
      /** Signed gst1 token — the buyer's bearer credential until it expires. */
      token: z.string().min(1),
      customerId: zId,
      expiresAt: zEpochMs,
      /** B52 — the channel's brand theme rides the buyer's first call. */
      theme: zChannelTheme.optional(),
      /**
       * B397 leg (3) — the channel's provider publishable key rides the mint
       * (same delivery seam as the theme: the mint already reads the row).
       * The deferred cart & pay page constructs Stripe(pk) BEFORE any order
       * exists; a key arriving only on intent.create arrives an order too
       * late. Public by design — the same anonymous caller already receives
       * it on payment.intent.create. Honest absence when unconfigured.
       */
      publishableKey: z.string().min(1).optional(),
      /** B91 GAP-6 — the tenant's ONBOARDED connected account: Stripe.js mounts on it. */
      stripeAccount: z.string().min(1).optional(),
      /**
       * B403 #26 — the tenant's stored business name (ops-config's
       * B125 step-0 fact), for the storefront header and footer 75:2
       * draws. Public by nature: it is the name the tenant shows every
       * anonymous buyer. Honest absence when never stored.
       */
      businessName: z.string().min(1).optional(),
      /**
       * B653 (c) — where guests find the venue (ops-config's facts): the
       * storefront foot says them beside the business name (553:2). Public
       * by nature — the tenant prints them for every buyer. Honest absence.
       */
      venueAddress: z.string().min(1).optional(),
      venuePhone: z.string().min(1).optional(),
      venueNote: z.string().min(1).optional(),
      /**
       * B403 #22-extension — the tenant's stored TYPE (ops-config's B126
       * fact), so buyer sentences speak the tenant's own vocabulary pack
       * ("on the same sailing"). Public by nature: the pack's words
       * already reach every anonymous buyer on every surface. Honest
       * absence when the tenant never chose a type.
       */
      tenantType: z.enum(["FERRY", "SHOW", "PARK", "ATTRACTION", "RETAIL"]).optional(),
      /**
       * B524 leg 2 — the tenant's GTM container, for the HOSTED page to load
       * (the embedded frame never does — it relays to the host's container).
       * Public by nature: the id is printed in every page that loads it.
       */
      gtmContainerId: z.string().regex(GTM_CONTAINER_ID).optional(),
      /** B818 — the memberships module is on (the page then asks whether a plan is on sale before it draws the door). */
      memberships: z.literal(true).optional(),
      /**
       * B549 — the channel's group-booking threshold (default 20 applied
       * server-side; absent = OFF): the stepper stops at threshold−1 and
       * the sheet offers the groups interrupt instead of a refused add.
       */
      groupThreshold: z.number().int().min(1).optional(),
      /**
       * B549 (9) — the first name the groups desk signs with, riding beside
       * the threshold (absent when the channel names none, or has no
       * interrupt to sign).
       */
      groupsContactName: z.string().min(1).max(40).optional(),
      /**
       * B621 — the tenant's CLOCK facts (ops-config's B125 timezone and the
       * ruled timeFormat), so every buyer time speaks the TENANT's zone and
       * chosen face from the first call. Public by nature; honest absence
       * when never stored — 12h AM/PM is the reader's ruled default.
       */
      timezone: z.string().min(1).optional(),
      timeFormat: z.enum(["12h", "24h"]).optional(),
    })
    .strict(),
  exampleRequest: { channelId: "web" },
  exampleResponse: { token: "gst1.eyJleGFtcGxlIjp0cnVlfQ.key-1.c2ln", customerId: "guest-1", expiresAt: 1_780_001_800_000 },
});

/**
 * B632 (a) — the guest MAGIC LINK's request door (RULED by the design seat
 * 2026-10-06 under Pathik's standing instruction; Pathik may overrule).
 * Public and UNIFORM: the answer is `{ ok: true }` whether or not the
 * address has bookings — rate caps (per email + per transport source) and
 * the origin wall are the door's walls, and the append-only request log is
 * its audit trail. The mail carries a signed single-use link (15-minute
 * TTL) that session.magiclink.open exchanges for a CUSTOMER session.
 */
export const sessionMagicLinkRequest = publicOp({
  name: "session.magiclink.request",
  permission: "public",
  fields: { email: z.string().min(3).max(254), channelId: zId },
  response: z.object({ ok: z.literal(true) }).strict(),
  exampleRequest: { email: "buyer@example.com", channelId: "web" },
  exampleResponse: { ok: true },
});

/**
 * B632 (a) — the magic link's open door: verifies the signed link, spends
 * its single use atomically, and mints an ordinary gst1 CUSTOMER session
 * bound to the EMAIL's customer row (§4.1 keys buyers by email, so the
 * B25c fence over that id IS "every order whose buyer email matches").
 * Every refusal is ONE uniform shape — a spent, expired, forged and
 * never-minted link are indistinguishable to the caller.
 */
export const sessionMagicLinkOpen = publicOp({
  name: "session.magiclink.open",
  permission: "public",
  fields: { token: z.string().min(1).max(4096) },
  response: z
    .object({
      /** Signed gst1 token — the buyer's bearer credential until it expires. */
      token: z.string().min(1),
      customerId: zId,
      email: z.string().min(3),
      expiresAt: zEpochMs,
    })
    .strict(),
  exampleRequest: { token: "mlk1.eyJleGFtcGxlIjp0cnVlfQ.key-1.c2ln" },
  exampleResponse: {
    token: "gst1.eyJleGFtcGxlIjp0cnVlfQ.key-1.c2ln",
    customerId: "cust-1",
    email: "buyer@example.com",
    expiresAt: 1_782_592_000_000,
  },
});
